<template>
  <div class="sepa-payment">
    <small v-if="$tm('payment.methods.sepa.msg')[0]">{{ $t('payment.methods.sepa.msg') }}</small>
    <div>
      <FormField
        :label="showAsteriskIfRequired('iban') + $t('donationForm.details.sepa.iban.label')"
        v-model="sepa.iban"
        :validation="v$.sepa.iban"
        theme="light"
      />
    </div>
    <div
      class="confirm-payment-holder"
      v-if="$l10n.currentLocation.formFields.confirmPaymentHolderRequired"
    >
      <CheckBox
        :label="$t('payment.confirmPaymentHolder')"
        v-model="assumedPaymentHolder"
      />
    </div>
    <SubmitButton @submitted="processDonation" />
  </div>
</template>

<script>
import { handleError, handleSuccess } from '@/lib/payment/handlers'
import awaitUpdate from '@/lib/donation/awaitUpdate'
import { ref } from 'vue'
import { required } from '@vuelidate/validators'
import { useDonationStore } from '@/stores/donation'
import { usePaymentStore } from '@/stores/payment'
import { useRootStore } from '@/stores/root'
import { v4 as uuid } from 'uuid'
import axios from 'axios'
import CheckBox from '@/components/form/CheckBox'
import FormField from '@/components/form/FormField'
import { isValidIBAN } from 'ibantools'
import SubmitButton from '@/components/form/SubmitButton'
import useVuelidate from '@vuelidate/core'

export default {
  name: 'SepaComponent',
  components: { FormField, CheckBox, SubmitButton },
  setup () {
    const donationStore = useDonationStore()
    const paymentStore = usePaymentStore()
    const rootStore = useRootStore()
    const sepa = ref({ iban: '' })
    const assumedPaymentHolder = ref(true)
    return {
      rootStore, donationStore, paymentStore, v$: useVuelidate(), sepa, assumedPaymentHolder
    }
  },
  validations: {
    sepa: {
      iban: {
        required,
        iban: value => isValidIBAN(value)
      }
    }
  },
  methods: {
    async processDonation () {
      const params = Object.assign({}, {
        ...this.donationStore.donationParams,
        payment: { adapter: 'sepa', iban_number: this.paymentStore.details.iban },
        location: this.$l10n.currentLocation.key,
        language: this.$l10n.currentLanguage,
        turnstile_response: this.rootStore.turnstileToken
      })

      let donationResponse = null
      try {
        donationResponse = await axios.post(`${process.env.VUE_APP_BACKEND_HOST}/donation`, params)
      } catch (error) {
        const failureMessage = error.response ? error.response.data : error
        const errMessage = (('message' in failureMessage) ? failureMessage.message : failureMessage)
        handleError(this, errMessage)
        return
      }

      const result = await awaitUpdate(this, donationResponse.data.reference)
      if (result.status === 'failed') {
        handleError(this, result.details)
        return
      }
      handleSuccess(this, this.thankYouPath)
    },
    showAsteriskIfRequired (field) {
      const fieldRequired = this.v$.sepa[field].required
      if (this.v$.sepa[field] === 'none') {
        return '* '
      }
      return (fieldRequired && fieldRequired.$invalid) ? '* ' : ''
    }
  },
  computed: {
    thankYouPath () { return this.$t('routeNames.thankYou') },
    sepaInvalid () {
      if (this.assumedPaymentHolder === false) { return true }
      return this.v$['sepa'] ? this.v$.sepa.$invalid : true
    }
  },
  watch: {
    'sepa.iban': function (val) {
      if (val) {
        this.sepa.iban = val.toUpperCase().replace(/[^A-Za-z0-9]/g, '')
        this.paymentStore.setDetails({ ...this.sepa })
      }
    },
    validateFields: function () { this.v$.$touch() },
    sepaInvalid: function (val) { this.paymentStore.setComplete(!val) }
  },
  mounted () {
    this.donationStore.setReference(uuid())
    this.paymentStore.setComplete(false)
    if (this.$l10n.currentLocation.formFields.confirmPaymentHolderRequired) { this.assumedPaymentHolder = false }
  }
}
</script>

<style lang="scss" scoped>
  a {
    color: var(--color-web-orange);
    &:visited {
      color: var(--color-web-orange);
    }
  }

  .confirm-payment-holder {
    margin-top: 0.5rem;
  }
</style>
